import { useCallback, useMemo } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import {
  selectMapEntityDetails,
  selectMapEntityVariant,
  selectServiceLocationId,
  setEntityDetails,
} from '@user/store/map/slice'
import { selectAllKnockStatuses } from '@user/store/knockStatus/slice'
import {
  addKnockStatusHistory,
  createKnockStatusLocation,
  createKnockStatusLocationBase,
  updateKnockStatusHistoryById,
} from '@user/store/knockStatus/api'
import { fetchEntityDetailAndProducts } from '@user/store/map/api'
import { PESTPAC_ENTITY_TYPE } from '@user/store/map/pestPacPin'
import {
  KnockStatusDetails,
  KnockStatusHistoryInput,
  KnockStatusLocationBaseInput,
  UpdateKnockStatusHistoryInput,
} from '@user/store/knockStatus/typings'
import { toast } from '@sales-center/components'
import { selectCurrentUser } from '@user/store/authorization'
import { saveGeneralPreferencesBatch } from '@user/store/userPreferences/userPreferencesGeneral'
import { useFilterChipData } from '@user/components/HereMap/hooks/useFilterChipData'
import {
  computeFilterUpdateForNewRecord,
  FILTERS_AUTO_UPDATED_KNOCK_STATUS,
} from '@user/features/MapView/RecordsHiddenByFilters/filterUpdateForNewRecord'
import { FormValues } from './useData'
import { EntityLocationDTO } from '@user/store/map/typings'
import { hasNoBackingEntity } from './entityLocationHelpers'

const mapVariantToEntityType = (variant: string | null): string => {
  switch (variant?.toLowerCase()) {
    case 'opportunity':
      return 'Opportunity'
    case 'lead':
      return 'Lead'
    case 'pendinglead':
    case 'draftlead':
      return 'DraftLead'
    case 'knockstatus':
      return 'KnockStatus'
    default:
      return 'Unknown'
  }
}

// SH-1176: resolves the location id and "added by" name from a knock-status
// mutation response, for both the first-add (POST) and subsequent-add (PUT)
// branches. Extracted as a pure function so it's testable without
// React/Redux wiring — see useHandlers.test.tsx.
export function resolveKnockStatusSubmitResult(
  hasExistingHistory: boolean,
  existingKnockStatusLocationId: number | undefined,
  mutationResult: KnockStatusDetails | undefined,
): { knockStatusLocationId: number; createdByName?: string } {
  // Prefer the id from the mutation response — it's always present on
  // success, and is the only source of truth when the client didn't already
  // know the location id (e.g. a PUT resolved purely via contactLocationId).
  // Fall back to the existing client-side id only if the response omits it.
  const fallbackKnockStatusLocationId = hasExistingHistory
    ? existingKnockStatusLocationId
    : undefined

  return {
    knockStatusLocationId: Number(
      mutationResult?.knockStatusLocationId ?? fallbackKnockStatusLocationId,
    ),
    createdByName: mutationResult?.createdByName,
  }
}

// WWM-18096: which entity type the filter calculator should judge this pin as. The `entityType`
// derived in the hook collapses everything without a backing entity to 'KnockStatus', which is
// right for the save payload but wrong here: a PestPac pin also has no backing entity, and the two
// are filtered by different rules. A knock pin whose status isn't selected is hidden even when the
// selection is empty; a PestPac pin is only hidden once the selection is non-empty
// (recordsHiddenByFilters.classifyRecordVisibility, mirroring AttachKnockStatusesToPestPacPins
// server-side). Judging a PestPac pin as 'knockstatus' would narrow a never-configured filter down
// to a single id on a save that needed no reveal at all.
//
// Pure so it's testable without React/Redux wiring — same reasoning as
// resolveKnockStatusSubmitResult above.
export function resolveEntityTypeForFilters(
  hasNoEntity: boolean,
  addressEntityType: string | null | undefined,
  derivedEntityType: string,
): string {
  if (!hasNoEntity) return derivedEntityType.toLowerCase()

  const fromAddress = addressEntityType?.toLowerCase()
  // 'Unknown' is what convertHereCoordsToEntityLocation stamps on a bare rooftop click
  // (HereMap/hooks/useData.ts) — a first knock on a house with nothing behind it. The hook's own
  // entityType derivation already reads it as KnockStatus; do the same here, or the calculator
  // matches no known type, reports the pin as visible, and silently skips the reveal.
  if (!fromAddress || fromAddress === 'unknown') return 'knockstatus'
  return fromAddress
}

const sanitizeCustomReason = (
  reason: string | null | undefined,
): string | null => {
  if (!reason) return null
  const trimmed = reason.trim()
  return trimmed === '' ? null : trimmed
}

type Props = {
  onClose: (hasExistingHistory?: boolean) => void
  reload?: () => void
  hasExistingHistory: boolean
  currentKnockStatusName?: string | null
  onRefresh?: () => void
  address: EntityLocationDTO | null
  onSaveNewKnockStatusLocation?: (
    newKnockLocation: KnockStatusLocationBaseInput,
  ) => void
}

export function useHandlers({
  onClose,
  reload,
  hasExistingHistory,
  currentKnockStatusName,
  onRefresh,
  address,
  onSaveNewKnockStatusLocation,
}: Props) {
  const dispatch = useAppDispatch()
  const entityDetails = useAppSelector(selectMapEntityDetails)
  const variant = useAppSelector(selectMapEntityVariant)
  const serviceLocationId = useAppSelector(selectServiceLocationId)
  const allStatuses = useAppSelector(selectAllKnockStatuses)
  const currentUser = useAppSelector(selectCurrentUser)
  const { selectedTypes, selectedKnockStatusIds, myRecords, d2dKnockEnabled } =
    useFilterChipData()

  const selectedTypesSig = selectedTypes.join('_')
  const selectedKnockStatusIdsSig = selectedKnockStatusIds.join('_')
  const activeMapFilters = useMemo(
    () => ({ selectedTypes, selectedKnockStatusIds, myRecords }),
    [selectedTypesSig, selectedKnockStatusIdsSig, myRecords],
  )

  // Fall back to address.entityType, then 'KnockStatus' when Redux variant is
  // not loaded. Standalone KS pins have no backing entity so variant is always
  // null — 'KnockStatus' is always the correct fallback (SH-851, SH-857).
  const rawEntityType = mapVariantToEntityType(
    variant ?? address?.entityType ?? null,
  )
  const entityType = rawEntityType === 'Unknown' ? 'KnockStatus' : rawEntityType
  const isDraftLead = entityType !== 'Lead' && entityType !== 'Opportunity'

  const knockStatusLocationId = useMemo(() => {
    return isDraftLead
      ? entityDetails?.knockStatusDetails?.knockStatusLocationId
      : entityDetails?.locations.serviceLocations.find(
          (item) => item.id === serviceLocationId,
        )?.knockStatusDetails?.knockStatusLocationId
  }, [entityDetails, isDraftLead, serviceLocationId])

  const dispatchEntityKnockStatusUpdate = useCallback(
    (knockStatusDetails: {
      knockStatusLocationId: number
      knockStatusSettingsId: number
      knockStatusSettingsName: string
      mostRecentNote: string
      createdTime: Date
      createdByName?: string
      knockStatusCount: number
    }) => {
      if (!entityDetails) return

      if (!isDraftLead) {
        dispatch(
          setEntityDetails({
            ...entityDetails,
            locations: {
              ...entityDetails.locations,
              serviceLocations: entityDetails.locations.serviceLocations.map(
                (loc) => {
                  if (loc.id === serviceLocationId) {
                    return {
                      ...loc,
                      knockStatusDetails: {
                        ...loc.knockStatusDetails,
                        ...knockStatusDetails,
                      },
                    }
                  }
                  return loc
                },
              ),
            },
          }),
        )
      } else {
        dispatch(
          setEntityDetails({
            ...entityDetails,
            knockStatusDetails: {
              ...entityDetails.knockStatusDetails,
              ...knockStatusDetails,
            },
          }),
        )
      }
    },
    [dispatch, entityDetails, isDraftLead, serviceLocationId],
  )

  const onModalClose = useCallback(
    (hasExistingHistory: boolean) => {
      onClose(hasExistingHistory)
    },
    [onClose],
  )

  // WWM-18096: saving a knock status that the map filter hides makes the pin the user just
  // touched disappear. Widen the filter to include it, and report whether the preference write
  // actually landed so the caller can gate its toast on that (never on `changed` alone — the
  // preferences slice only upserts on .fulfilled).
  //
  // Deliberately called BEFORE the mutation: a knock-status save already triggers two forced
  // refreshes (the knockStatusSavedAt effect in ExploratoryMapNew/hooks/useApi.ts and onRefresh),
  // and both capture the filter as it is when they run. Writing the preference afterwards leaves
  // those two carrying the stale filter while a third, debounced refresh carries the new one, so
  // the pin blinks out for ~500ms before returning. The caller still holds the toast until the
  // mutation succeeds, so a failed save can't announce a filter change for a status never saved.
  const applyKnockStatusFilterReveal = useCallback(
    async (
      entityTypeForFilters: string,
      statusId: string | undefined,
      ownerId: string | null,
    ): Promise<boolean> => {
      if (!d2dKnockEnabled || !statusId) return false

      const { batch, changed } = computeFilterUpdateForNewRecord(
        {
          entityType: entityTypeForFilters,
          knockStatusSettingsId: statusId,
          ownerId,
        },
        activeMapFilters,
        currentUser?.userId ?? null,
      )
      if (!changed) return false

      const action = await dispatch(saveGeneralPreferencesBatch(batch))
      return saveGeneralPreferencesBatch.fulfilled.match(action)
    },
    [d2dKnockEnabled, activeMapFilters, currentUser, dispatch],
  )

  const onSubmit = useCallback(
    async (values: FormValues, { resetForm }: any = {}) => {
      const statusEntry = allStatuses.find((s) => s.name === values.knockStatus)
      const statusId = statusEntry?.id

      if (!entityDetails && !address) return

      const hasNoEntity = hasNoBackingEntity(address)

      const filtersUpdated = await applyKnockStatusFilterReveal(
        resolveEntityTypeForFilters(
          hasNoEntity,
          address?.entityType,
          entityType,
        ),
        statusId,
        address?.assigneeId ?? null,
      )
      if (hasNoEntity) {
        // Flag PestPac pins so the backend skips the standalone ContactLocation (no duplicate pin).
        const isExternalPin =
          address?.entityType?.toLowerCase() === PESTPAC_ENTITY_TYPE
        const baseInput = {
          knockStatusSettingsId: statusId || null,
          customReason: sanitizeCustomReason(values.note),
          isActive: true,
          isExternalPin,
          knockStatusLocationAddress: {
            street1: address?.street1 || '',
            street2: address?.street2 || '',
            city: address?.city || '',
            stateAbbreviation: address?.stateAbbreviation || '',
            postalCode: address?.postalCode || '',
            country: address?.country || '',
            latitude: address?.latitude || 0,
            longitude: address?.longitude || 0,
          },
        }

        try {
          if (hasExistingHistory) {
            await dispatch(
              addKnockStatusHistory({
                id: address?.knockStatusLocationId,
                knockStatusSettingsId: statusId || null,
                customReason: sanitizeCustomReason(values.note),
                knockStatusName: statusEntry?.name || null,
                previousKnockStatusName: currentKnockStatusName || null,
                isActive: true,
                entityId: 0,
                entityType: 'knockstatus',
                contactLocationId: address?.contactLocationId,
                isExternalPin,
              }),
            ).unwrap()
            // Push the just-saved status/reason into the open preview bubble so
            // it updates immediately, without depending on the tile refetch
            // re-resolving the bubble's matched location. Mirrors the create
            // branch below. A cleared reason (empty note) must overwrite the
            // previous one — hence customReason: values.note || null. (SH-XXX)
            onSaveNewKnockStatusLocation?.({
              ...baseInput,
              id: address?.knockStatusLocationId,
              knockStatusSettingName: statusEntry?.name || '',
            })
          } else {
            await dispatch(createKnockStatusLocationBase(baseInput))
              .unwrap()
              .then((res: KnockStatusLocationBaseInput) => {
                if (res) {
                  onSaveNewKnockStatusLocation?.({
                    ...res,
                    knockStatusSettingName: statusEntry?.name || '',
                  })
                }
              })
          }
          toast.success('Knock status saved successfully')
          if (filtersUpdated) {
            toast.success(FILTERS_AUTO_UPDATED_KNOCK_STATUS)
          }
          if (resetForm) resetForm()
          if (reload) reload()
          onRefresh?.()
          onModalClose(hasExistingHistory)
        } catch (e: any) {
          toast.error(e?.message || 'Failed to save knock status')
        }

        return
      }

      if (!entityDetails) return

      const input: KnockStatusHistoryInput = {
        knockStatusSettingsId: statusId || null,
        customReason: sanitizeCustomReason(values.note),
        entityType,
        entityId: entityDetails.id,
        isActive: true,
        knockStatusName: statusEntry?.name || null,
      }

      if (!isDraftLead && serviceLocationId) {
        input.serviceLocationId = serviceLocationId
      } else {
        input.knockStatusLocationAddress = {
          street1: entityDetails.street1 || '',
          street2: entityDetails.street2 || '',
          city: entityDetails.city || '',
          stateAbbreviation: entityDetails.stateAbbreviation || '',
          postalCode: entityDetails.postalCode || '',
          country: entityDetails.country || '',
          latitude: entityDetails.latitude || 0,
          longitude: entityDetails.longitude || 0,
        }
      }

      try {
        let mutationResult: KnockStatusDetails | undefined

        if (hasExistingHistory) {
          if (!knockStatusLocationId && !address?.contactLocationId) return
          // Entity already has a knock status location – only add a new history record
          input.id = knockStatusLocationId ?? 0
          input.previousKnockStatusName = currentKnockStatusName || null
          input.contactLocationId = address?.contactLocationId
          mutationResult = await dispatch(addKnockStatusHistory(input)).unwrap()
        } else {
          // First time – create a new knock status location + first history record
          mutationResult = await dispatch(
            createKnockStatusLocation(input),
          ).unwrap()
        }

        const {
          knockStatusLocationId: resolvedKnockStatusLocationId,
          createdByName,
        } = resolveKnockStatusSubmitResult(
          hasExistingHistory,
          knockStatusLocationId,
          mutationResult,
        )

        // Always update entity details in the store
        const existingKnockStatusCount = isDraftLead
          ? (entityDetails.knockStatusDetails?.knockStatusCount ?? 0)
          : (entityDetails.locations.serviceLocations.find(
              (loc) => loc.id === serviceLocationId,
            )?.knockStatusDetails?.knockStatusCount ?? 0)

        dispatchEntityKnockStatusUpdate({
          knockStatusLocationId: resolvedKnockStatusLocationId,
          knockStatusSettingsId: Number(statusId) || 0,
          knockStatusSettingsName: statusEntry?.name || '',
          mostRecentNote: input.customReason || '',
          createdTime: new Date(),
          createdByName,
          knockStatusCount: hasExistingHistory
            ? existingKnockStatusCount + 1
            : 1,
        })

        // The optimistic patch above has no real KnockStatusHistory id (the
        // add/create response doesn't return one). Editing the reason later
        // needs that id, so refetch entity details directly here rather than
        // relying solely on onRefresh -- onRefresh's repair path only fires
        // when EntityDetailsDrawer happens to be mounted (its refreshTrigger
        // effect), which isn't the case when this modal is opened straight
        // from the map preview bubble without the big drawer ever opening.
        if (variant) {
          dispatch(
            fetchEntityDetailAndProducts([
              variant,
              entityDetails.id,
              serviceLocationId ?? 0,
              true,
            ]),
          )
        }

        toast.success('Knock status saved successfully')
        if (filtersUpdated) {
          toast.success(FILTERS_AUTO_UPDATED_KNOCK_STATUS)
        }
        if (resetForm) resetForm()
        onRefresh?.()
        if (reload) reload()
        onModalClose(hasExistingHistory)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to save knock status')
      }
    },
    [
      applyKnockStatusFilterReveal,
      allStatuses,
      entityDetails,
      address,
      hasExistingHistory,
      reload,
      onRefresh,
      onModalClose,
      dispatch,
      knockStatusLocationId,
      currentKnockStatusName,
      entityType,
      isDraftLead,
      serviceLocationId,
      variant,
      dispatchEntityKnockStatusUpdate,
      onSaveNewKnockStatusLocation,
    ],
  )

  const onUpdate = useCallback(
    async (args: {
      id: number
      customReason: string | null
      knockStatusName: string | null
      previousKnockStatusName: string | null
    }) => {
      if (!entityDetails && !address) return

      const raw = mapVariantToEntityType(variant ?? address?.entityType ?? null)
      const resolvedEntityType = raw === 'Unknown' ? 'KnockStatus' : raw

      const sanitizedCustomReason = sanitizeCustomReason(args.customReason)

      const input: UpdateKnockStatusHistoryInput = {
        customReason: sanitizedCustomReason,
        knockStatusName: args.knockStatusName,
        previousKnockStatusName: args.previousKnockStatusName,
        entityType: resolvedEntityType,
        entityId: entityDetails?.id ?? 0,
      }

      try {
        await dispatch(
          updateKnockStatusHistoryById({
            id: args.id,
            entityType: resolvedEntityType,
            input,
          }),
        ).unwrap()

        // Only update entity details if the updated history entry matches the current entity knock status
        const currentEntityKnockStatusName = isDraftLead
          ? entityDetails?.knockStatusDetails?.knockStatusSettingsName
          : entityDetails?.locations.serviceLocations.find(
              (loc) => loc.id === serviceLocationId,
            )?.knockStatusDetails?.knockStatusSettingsName

        const isUpdatingCurrentKnockStatus =
          args.previousKnockStatusName === currentEntityKnockStatusName

        if (isUpdatingCurrentKnockStatus) {
          const statusEntry = allStatuses.find(
            (s) => s.name === args.knockStatusName,
          )
          const currentLocationId = isDraftLead
            ? (entityDetails?.knockStatusDetails?.knockStatusLocationId ?? 0)
            : (entityDetails?.locations.serviceLocations.find(
                (loc) => loc.id === serviceLocationId,
              )?.knockStatusDetails?.knockStatusLocationId ?? 0)
          const currentCount = isDraftLead
            ? (entityDetails?.knockStatusDetails?.knockStatusCount ?? 1)
            : (entityDetails?.locations.serviceLocations.find(
                (loc) => loc.id === serviceLocationId,
              )?.knockStatusDetails?.knockStatusCount ?? 1)

          dispatchEntityKnockStatusUpdate({
            knockStatusLocationId: currentLocationId,
            knockStatusSettingsId: Number(statusEntry?.id) || 0,
            knockStatusSettingsName: args.knockStatusName || '',
            mostRecentNote: sanitizedCustomReason || '',
            createdTime: new Date(),
            knockStatusCount: currentCount,
          })
        }

        toast.success('Knock status reason updated successfully')
        if (reload) reload()
        onRefresh?.()
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update knock status reason')
      }
    },
    [
      dispatch,
      entityDetails,
      variant,
      reload,
      onRefresh,
      allStatuses,
      isDraftLead,
      serviceLocationId,
      dispatchEntityKnockStatusUpdate,
    ],
  )

  return {
    onSubmit,
    onUpdate,
    onModalClose,
  }
}
